import {
    createContext,
    createRef,
    FC,
    PropsWithChildren,
    useContext,
} from 'react';

import useVideoPlayer, { UseVideoPlayerReturn } from '../hooks/useVideoPlayer';
import { SanitizedStoryPage } from '../types';
import { usePageContext } from './PageContext';
import { useProgressContext } from './ProgressContext';

interface Props extends PropsWithChildren {
    storyPage: SanitizedStoryPage;
}

const DEFAULT_VIDEO_PLAYER_CONTEXT: UseVideoPlayerReturn = {
    vid: createRef<HTMLVideoElement>(),
    playVisible: false,
    replayVisible: false,
    videoHasAudio: false,
    isMuted: false,
    isLoaded: false,
    isPaused: false,
    onCanPlayThrough: () => {},
    onEnded: () => {},
    playVideo: () => {},
    pauseVideo: () => {},
    restartVideo: () => {},
    buttons: [],
    isRtl: false,
};

export const VideoPlayerContext = createContext<UseVideoPlayerReturn>(
    DEFAULT_VIDEO_PLAYER_CONTEXT
);

export const VideoPlayerProvider: FC<Props> = ({ children, storyPage }) => {
    const { currentPageIndex } = useProgressContext();
    const { pageIndex } = usePageContext();
    const hook = useVideoPlayer({
        storyPage,
        autoPlay: true,
        isCurrent: pageIndex === currentPageIndex,
    });

    return (
        <VideoPlayerContext.Provider value={hook}>
            {children}
        </VideoPlayerContext.Provider>
    );
};

export const useVideoPlayerContext = () =>
    useContext<UseVideoPlayerReturn>(VideoPlayerContext);
